import React, { useMemo, useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { faMagnifyingGlass } from "@performyard/fontawesome/pro-regular-svg-icons";
import { ButtonStandard, ModalBase, MultipleSelectList, Spinner, Text } from "@performyard/py-components";
import type { Option } from "@performyard/py-components";

import { useSignatureRequestTemplates } from "../../../api/hooks/defs/signature-request-templates";
import useDebouncedValue from "../../../hooks/useDebouncedValue";

/**
 * The list contract's maximum page size. Search runs server-side (`name`), so this caps
 * how many *matches* are shown rather than how many templates are searchable — a company
 * with more than 100 templates can still reach any of them by typing.
 */
const TEMPLATE_LIMIT = 100;

/**
 * The list scrolls past this many pixels instead of growing. Below it the modal hugs its
 * rows, matching the groups modal in the compensation employee picker.
 */
const LIST_MAX_HEIGHT = 320;

export type SignatureTemplateSelection = {
	id: string;
	name: string;
};

/**
 * Renders one option as the Figma frames draw it: a 32px row of plain text on the
 * alternating neutral-0/neutral-1 background `MultipleSelectList` paints, with the
 * design's 8px inline padding. There is no radio or checkbox — the whole row is the hit
 * target, and the chosen one is called out with a primary-1 fill and semi-bold text.
 */
const renderTemplateOption = (option: Option<string>, isSelected: boolean, onToggle: () => void) => (
	<button
		type="button"
		role="option"
		aria-selected={isSelected}
		onClick={onToggle}
		className={`flex h-full w-full min-w-0 cursor-pointer items-center border-none px-2 text-left ${
			isSelected ? "bg-primary-1" : "bg-transparent hover:bg-neutral-2"
		}`}
		data-testid={`signature-template-option-${option.value}`}
	>
		<Text
			size="paragraph-3"
			weight={isSelected ? "semi-bold" : "regular"}
			color="neutral-9"
			wrap={false}
			dangerouslySetClassName="min-w-0 py-text-truncate"
		>
			{option.label}
		</Text>
	</button>
);

/**
 * Pick a signature request template to attach to a to-do. Built on the same
 * `MultipleSelectList` the design names, so it inherits the striped rows, thin scrollbar
 * and search field the employee/form/group selection modals use. Selection is
 * single-value, and the footer's Select button confirms so a stray click can't attach
 * the wrong document.
 */
export const SignatureTemplatePickerModal = ({
	onClose,
	onSelect,
}: {
	onClose: () => void;
	onSelect: (selection: SignatureTemplateSelection) => void;
}) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [selected, setSelected] = useState<SignatureTemplateSelection | null>(null);
	const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

	const { data, isLoading } = useSignatureRequestTemplates({
		name: debouncedSearchTerm.trim() || undefined,
		limit: TEMPLATE_LIMIT,
		sortBy: "name",
		sortOrder: "asc",
	});

	const templates = useMemo(() => data?.signatureRequestTemplates ?? [], [data]);
	const truncated = (data?.totalCount ?? 0) > templates.length;

	const options = useMemo<Option<string>[]>(
		() => templates.map((template) => ({ label: template.name, value: template._id })),
		[templates]
	);

	/**
	 * `MultipleSelectList` toggles values in and out of an array. Keeping the one value the
	 * user just added collapses that to single-select; clicking the already-selected row
	 * yields no new value, which leaves the current selection in place rather than clearing it.
	 */
	const handleChange = (values: string[]) => {
		const nextId = values.find((value) => value !== selected?.id);
		if (!nextId) return;

		const template = templates.find(({ _id }) => _id === nextId);
		if (template) setSelected({ id: template._id, name: template.name });
	};

	const noResults = !isLoading && options.length === 0;

	return (
		<ModalBase isOpen onClose={onClose} dangerouslySetClassName="w-[432px] max-w-[90vw] max-h-[600px]">
			<div className="px-6 pb-4">
				<div className="text-center">
					<Text size="sub-heading" weight="semi-bold">
						Select Signature Template
					</Text>
				</div>
			</div>

			<div className="px-6">
				<div
					className={`relative [&_[role=listbox]]:![scrollbar-color:#9197A1_#FFFFFF] ${
						noResults ? "min-h-[208px]" : ""
					}`}
				>
					<MultipleSelectList<string>
						options={isLoading ? [] : options}
						value={selected ? [selected.id] : []}
						onChange={handleChange}
						searchPlaceholder="Search Signature Templates..."
						searchValue={searchTerm}
						onSearchChange={setSearchTerm}
						renderOption={renderTemplateOption}
						virtualizationConfig={{ itemHeight: 32, maxHeight: LIST_MAX_HEIGHT }}
					/>

					{/*
					 * These cover `MultipleSelectList`'s own one-line empty text rather than replace
					 * it, so the empty state is the only case that reserves height. With rows to
					 * show the modal hugs them and the list scrolls at `LIST_MAX_HEIGHT`.
					 */}
					{noResults && (
						<div className="absolute inset-x-0 top-14 bottom-0 flex flex-col items-center justify-center gap-2 bg-neutral-0 px-6 py-6 text-center">
							{debouncedSearchTerm.trim() ? (
								<>
									<div className="flex items-center justify-center rounded-full bg-neutral-1 p-3">
										<FontAwesomeIcon icon={faMagnifyingGlass} className="size-5 text-neutral-5" />
									</div>
									<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
										No Results Found
									</Text>
								</>
							) : (
								<Text size="paragraph-3" weight="regular" color="neutral-6">
									No signature request templates found. Create one from the Signature Requests page
									first.
								</Text>
							)}
						</div>
					)}

					{isLoading && (
						<div className="absolute inset-0 top-14 flex items-center justify-center bg-neutral-0">
							<Spinner size={32} />
						</div>
					)}
				</div>

				{truncated && (
					<div className="mt-2">
						<Text size="caption" weight="regular" color="neutral-6">
							{`Showing the first ${TEMPLATE_LIMIT} matches by name. Keep typing to narrow the list.`}
						</Text>
					</div>
				)}
			</div>

			<div className="sticky bottom-0 bg-neutral-0 px-6 pt-6 pb-6">
				<div className="flex justify-center gap-2">
					<div className="w-fit">
						<ButtonStandard variant="outline" color="neutral" size="large" onClick={onClose}>
							Cancel
						</ButtonStandard>
					</div>
					<div className="w-fit">
						<ButtonStandard
							variant="solid"
							color="primary"
							size="large"
							disabled={!selected}
							onClick={() => selected && onSelect(selected)}
							data-testid="signature-template-select"
						>
							Select
						</ButtonStandard>
					</div>
				</div>
			</div>
		</ModalBase>
	);
};
